import { useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../context/AuthContext';
import {
  buildJourney, journeyToRows, JOURNEY_COLUMNS,
} from '../../data/journey';
import { toCsv } from '../../utils/csv';

const fmt = (ts) => (ts ? new Date(ts).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

// Sales Journey / History (CRM revisions Task 5) — OWNER-ONLY. Reads the single
// event store (auditLogs) + lead docs and renders the full per-lead lifecycle,
// generated on demand and exportable to CSV. Managers/Agents are blocked here
// (UI gate) and agents are denied auditLogs at the rules layer too.
export default function SalesJourney() {
  const { tenantId, role } = useAuth();
  const isOwner = role === 'owner' || role === 'hqadmin';
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedAt, setGeneratedAt] = useState(null); // BUG-022: freshness stamp

  if (!isOwner) {
    return (
      <div className="card card-pad" style={{ maxWidth: 560 }}>
        <b>Owner-only.</b>
        <p className="muted" style={{ fontSize: 'var(--text-small)', marginTop: 8 }}>
          The Sales Journey history is restricted to the business owner to protect customer data and company secrets.
        </p>
      </div>
    );
  }

  async function generate() {
    if (!tenantId) return;
    setLoading(true); setError('');
    try {
      const [leadSnap, auditSnap] = await Promise.all([
        getDocs(query(collection(db, 'leads'), where('tenantId', '==', tenantId))),
        getDocs(query(collection(db, 'auditLogs'), where('tenantId', '==', tenantId))),
      ]);
      const leads = leadSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const audit = auditSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setEvents(buildJourney(leads, audit));
      setGeneratedAt(Date.now());
    } catch (e) {
      setError(e.message || 'Could not generate the sales journey.');
    } finally {
      setLoading(false);
    }
  }

  function downloadCsv() {
    const rows = journeyToRows(events || []);
    const csv = toCsv(rows, JOURNEY_COLUMNS);
    const stamp = new Date().toISOString().slice(0, 10);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `presto-sales-journey-${tenantId}-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div className="toolbar" style={{ marginBottom: 'var(--space-4)' }}>
        <button className="btn btn-primary" onClick={generate} disabled={loading}>
          {loading ? 'Generating…' : 'Generate history'}
        </button>
        <button className="btn btn-ghost" onClick={downloadCsv} disabled={!events || events.length === 0}>
          Export CSV
        </button>
        {events && (
          <span style={{ marginLeft: 'auto', color: 'var(--color-fg-muted)', fontSize: 'var(--text-small)' }}>
            {events.length} events across all leads · Last generated {fmt(generatedAt)}
          </span>
        )}
      </div>

      {error && <div className="form-error">{error}</div>}

      {events && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>When</th><th>Lead</th><th>Actor</th><th>Event</th><th>Change</th><th>Current status</th></tr>
            </thead>
            <tbody>
              {/* Newest-first in the view so a fresh Generate surfaces recent
                  changes at the top (BUG-022); CSV export stays chronological. */}
              {events.slice().reverse().map((e, i) => (
                <tr key={`${e.leadId}-${e.ts}-${i}`} className="audit-row">
                  <td>{fmt(e.ts)}</td>
                  <td className="muted">{e.leadName || e.leadId}<br /><span style={{ fontSize: 'var(--text-caption)' }}>{e.leadId}</span></td>
                  <td>{e.actor}</td>
                  <td>{e.eventType}</td>
                  <td>
                    {e.eventType === 'Created' ? '—' : (
                      <><span className="diff-old">{String(e.oldValue || '—')}</span> → <span className="diff-new">{String(e.newValue || '—')}</span></>
                    )}
                  </td>
                  <td>{e.currentStatus}</td>
                </tr>
              ))}
              {events.length === 0 && (
                <tr><td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>No lifecycle events yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!events && !loading && (
        <p className="muted" style={{ fontSize: 'var(--text-small)' }}>
          Generate the full lifecycle history for every lead — created, assigned, reassigned, verified, status changes, archived and value edits — then export it to CSV.
        </p>
      )}
    </div>
  );
}
